/**
 * Data-table foundation shared by every CMS list (docs/06 §4): **server** pagination
 * (50 rows/page), server sorting and CSV export for viewers and above. TanStack Table owns
 * the column model only — paging and ordering are handed back to the caller so they end up in
 * the query string of the API call.
 */
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useT } from '../../i18n';
import { cn } from '../../lib/cn';
import type { CsvColumn } from '../../lib/csv';
import { formatNumber } from '../../lib/format';
import { RESULTS_ANCHOR_ID, useResultsAnchor } from '../layout/resultsAnchor';
import { Button } from '../ui/button';
import { Input } from '../ui/field';
import { ExportCsvButton, type ExportCsvButtonProps } from './ExportCsvButton';
import { ListError } from './ListError';

export const PAGE_SIZE = 50;

/**
 * How many rows PageDown/PageUp move the roving focus by (UX-578). A screenful of a 50-row page
 * rather than the page itself: the grid is server-paged, so jumping to the next *page* on
 * PageDown would be a fetch, not a move.
 */
export const GRID_PAGE_STEP = 10;

/**
 * The name the announcement gives a sort key: the visible column header when there is one, else
 * the `cms:table.sortKeys.*` wording, else — for a key nobody has worded — the id (UX-572).
 */
export function sortKeyLabel(
  id: string,
  header: unknown,
  /** Resolves `cms:table.sortKeys.<id>`, or the empty string when it is not worded. */
  lookup: (id: string) => string,
): string {
  if (typeof header === 'string' && header.trim()) return header;
  return lookup(id).trim() || id;
}

export interface CsvExport<T> {
  filename: string;
  columns: CsvColumn<T>[];
  /**
   * Rows to export. **Required**: exporting `data` would only ever write the page on screen
   * (QA 2026-09-14 CMS-22) — pass `fetchAllRowsResult(...)` over the screen's current filters.
   */
  rows: ExportCsvButtonProps<T>['rows'];
}

export interface DataTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  getRowId: (row: T) => string;
  /**
   * What this list is — "Canciones", "Dispositivos". It becomes the grid's own accessible name
   * ("Lista de Canciones") and the subject of every announcement.
   *
   * QA 2026-09-15 **UX-560**: the AX name of all seven grids was the empty string, so a screen
   * reader entered "a grid" with no idea which one.
   */
  label: string;
  total: number;
  /** 1-based, like `PageQuery` (docs/03 §2). */
  page: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  isLoading?: boolean;
  /**
   * A query is in flight over rows that are already on screen (`keepPreviousData`): the table
   * is `aria-busy` while it lasts, so a reader is not told the old rows are the answer
   * (QA 2026-09-15 **UX-561**).
   */
  isFetching?: boolean;
  /**
   * The list query failed. The table then says so instead of leaving the previous page on
   * screen — TanStack keeps the old rows while a query errors and the operator had no way of
   * telling that the filter it shows is not the filter the rows come from (QA 2026-09-14 CMS-56).
   */
  isError?: boolean;
  /** Re-runs the failed query (the "Retry" button of the error row). */
  onRetry?: () => void;
  onRowClick?: (row: T) => void;
  /**
   * Accessible name of the row as a whole — what a keyboard user hears before pressing Enter on
   * it ("Abrir «Cielito Lindo»"). Falls back to the row's own text when omitted.
   */
  rowLabel?: (row: T) => string;
  selectedRowId?: string | null;
  csv?: CsvExport<T>;
  emptyLabel?: ReactNode;
  toolbar?: ReactNode;
  /**
   * Column ids the **API** can order by (the `SORTABLE` map of the matching service). Only these
   * headers become buttons.
   *
   * QA 2026-09-15 **UX-535**: every screen already passed `sorting`/`onSortingChange` and put
   * `sort=&dir=` on the wire, but not one `<th>` was clickable — TanStack's `getCanSort()` is
   * `false` for a column with no `accessorFn`, and every CMS column is a display column with a
   * custom `cell`. The sortable set is therefore explicit instead of inferred.
   */
  sortableIds?: readonly string[] | ReadonlySet<string>;
}

export function DataTable<T>({
  columns,
  data,
  getRowId,
  label,
  total,
  page,
  pageSize = PAGE_SIZE,
  onPageChange,
  sorting = [],
  onSortingChange,
  isLoading = false,
  isFetching = false,
  isError = false,
  onRetry,
  onRowClick,
  rowLabel,
  selectedRowId,
  csv,
  emptyLabel,
  toolbar,
  sortableIds,
}: DataTableProps<T>) {
  const { t, i18n } = useT();
  useResultsAnchor();
  const sortable = sortableIds
    ? sortableIds instanceof Set
      ? sortableIds
      : new Set(sortableIds as readonly string[])
    : null;
  const canSort = (id: string) => Boolean(onSortingChange && sortable?.has(id));
  /**
   * A header click cycles asc → desc → asc; the first click on a new column starts ascending,
   * which is what an editor means by "order by title".
   */
  const toggleSort = (id: string) => {
    if (!onSortingChange) return;
    const current = sorting[0];
    onSortingChange([{ id, desc: current?.id === id ? !current.desc : false }]);
  };
  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    manualPagination: true,
    manualSorting: true,
    enableSorting: Boolean(onSortingChange),
    onSortingChange: (updater) => {
      if (!onSortingChange) return;
      onSortingChange(typeof updater === 'function' ? updater(sorting) : updater);
    },
    getRowId: (row) => getRowId(row),
    getCoreRowModel: getCoreRowModel(),
  });

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);
  // A failed query shows no rows at all: stale ones would be a silent lie about the filters.
  const bodyRows = isError ? [] : table.getRowModel().rows;
  /**
   * The first page is still in flight and there is no previous result to keep: `total` is 0
   * because nothing has been counted yet, not because the list is empty. Asserting "0–0 de 0
   * entradas · Página 1 de 1" on a 143k catalog is a lie the operator reads for seconds
   * (QA 2026-09-15 UX-201), so the footer says it is loading instead.
   */
  const pending = isLoading && !isError && data.length === 0;
  /** A request is out: either the first one or a refetch under the rows already on screen. */
  const busy = (isLoading || isFetching) && !isError;

  /**
   * QA 2026-09-15 **UX-561** — sorting, paging and filtering replaced all fifty rows under the
   * operator with **no live-region text in 60+ samples**: a sighted user sees the table move,
   * a screen-reader user presses Enter on "Ordenar por esta columna" and hears nothing.
   *
   * The grid therefore owns a polite region of its own (the toast region carries what the
   * operator *did*; this one carries what the list *is*) and writes "143.394 resultados ·
   * ordenado por Título (ascendente) · página 1 de 2868" into it whenever the answer changes —
   * never on the first load, which is already announced by the "Cargando…" row.
   */
  const sort = sorting[0];
  const sortedColumn = sort ? columns.find((column) => column.id === sort.id) : undefined;
  /**
   * QA 2026-09-15 **UX-572**: the 23 header-driven sorts were worded properly, but every grid
   * whose *default* sort key has no visible column fell through to the raw database name —
   * /access announced «436 resultados · ordenado por **created_at** (descendente) · página 1 de
   * 9» and /playlists «ordenado por **updated_at**». That is the line that fires after every
   * create, revoke, CSV import and voucher batch on the busiest screen in the console, i.e. the
   * sentence a screen-reader operator hears most often. Every sort key therefore carries a
   * plain-language name of its own; the raw id stays only as the last resort for a key nobody
   * has worded yet.
   */
  const sortedName = sort
    ? sortKeyLabel(sort.id, sortedColumn?.header, (id) =>
        t(`cms:table.sortKeys.${id}` as 'cms:table.sortKeys.created_at', { defaultValue: '' }),
      )
    : '';
  const announcement = sort
    ? t('cms:table.announceSorted', {
        total: formatNumber(total, i18n.language),
        column: sortedName,
        dir: sort.desc ? t('cms:table.dirDesc') : t('cms:table.dirAsc'),
        page,
        pages,
      })
    : t('cms:table.announceResults', {
        total: formatNumber(total, i18n.language),
        page,
        pages,
      });
  // What the answer *is*: a new page, a new order, a new filter (a different first row or a
  // different total) — but not a re-render with the same rows.
  const answer = `${page}|${sort?.id ?? ''}|${sort?.desc ?? ''}|${total}|${bodyRows[0]?.id ?? ''}|${bodyRows.length}`;
  const [status, setStatus] = useState('');
  const lastAnswer = useRef<string | null>(null);
  useEffect(() => {
    if (busy || isError) return;
    if (lastAnswer.current === answer) return;
    // The first settled answer is the page load itself; only what follows is a change.
    const isFirst = lastAnswer.current === null;
    lastAnswer.current = answer;
    if (!isFirst) setStatus(announcement);
    // `announcement` is derived from `answer`; re-running on its identity alone would re-announce
    // on a language change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answer, busy, isError]);

  /**
   * QA 2026-09-15 **UX-532** — not one row of the console could be reached with a keyboard: the
   * rows were click-only `<tr>`s with no `tabIndex` and no control inside, so sixty consecutive
   * Tabs on /songs, /artists, /playlists, /access, /users, /devices and /heroes went
   * filters → search → Exportar CSV → pager → sidebar and never entered the list. An operator
   * could create a song and not open one.
   *
   * The table is now a `grid` whose rows are the focus stops, with a **roving tabindex**: one Tab
   * enters the list on the selected (or first) row, ↑/↓/Home/End move between rows without
   * leaving it, Enter or Space opens the row, and one more Tab leaves for the pager. Fifty rows
   * therefore cost one tab stop, not fifty.
   */
  const [activeRow, setActiveRow] = useState(0);
  const rowCount = bodyRows.length;
  // A new page, a new filter or a new sort re-seeds the roving index instead of leaving it past
  // the end of a shorter page.
  useEffect(() => {
    setActiveRow((index) => (index < rowCount ? index : 0));
  }, [rowCount, page]);

  const body = useRef<HTMLTableSectionElement>(null);
  const focusRow = (index: number) => {
    const clamped = Math.max(0, Math.min(rowCount - 1, index));
    setActiveRow(clamped);
    body.current?.querySelector<HTMLElement>(`[data-table-row="${clamped}"]`)?.focus();
  };

  const onRowKeyDown = (event: ReactKeyboardEvent<HTMLTableRowElement>, row: T, index: number) => {
    if (!onRowClick) return;
    // A control inside the cell (a switch, a link) keeps its own keys.
    if (event.target !== event.currentTarget) return;
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        onRowClick(row);
        break;
      case 'ArrowDown':
        event.preventDefault();
        focusRow(index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusRow(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusRow(0);
        break;
      case 'End':
        event.preventDefault();
        focusRow(rowCount - 1);
        break;
      /**
       * QA 2026-09-15 **UX-578**: the grid consumed neither key, so PageDown scrolled the
       * document while `aria-rowindex` stayed on 2 — the focused row ended up off screen with
       * no visible focus anywhere. ↓ ↑ Home End all keep the row in view; these now do too.
       */
      case 'PageDown':
        event.preventDefault();
        focusRow(index + GRID_PAGE_STEP);
        break;
      case 'PageUp':
        event.preventDefault();
        focusRow(index - GRID_PAGE_STEP);
        break;
      default:
        break;
    }
  };

  /** The row the single tab stop lands on: the selected one, else the first. */
  const tabStop = (() => {
    if (selectedRowId) {
      const index = bodyRows.findIndex((row) => row.id === selectedRowId);
      if (index >= 0) return index;
    }
    return Math.min(activeRow, Math.max(0, rowCount - 1));
  })();

  return (
    <div className="flex flex-col gap-3">
      {(toolbar || csv) && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">{toolbar}</div>
          {csv && <ExportCsvButton filename={csv.filename} columns={csv.columns} rows={csv.rows} />}
        </div>
      )}

      {/* The grid's own polite region (UX-561). `atomic`, so the whole sentence is read as one. */}
      <div aria-live="polite" aria-atomic="true" className="sr-only" data-testid="table-status">
        {status}
      </div>

      <div className="overflow-x-auto rounded-[12px] border border-line bg-panel outline-none">
        <table
          /**
           * The "Saltar a los resultados" target (UX-568). QA 2026-09-15 **UX-576**: it used to
           * be the wrapper `<div id="list-results">`, which has no role and no accessible name,
           * so a screen-reader user arrived and heard nothing. It is the grid itself now —
           * named (UX-560) and counted (`aria-rowcount`) — so landing announces «Lista de
           * Canciones, tabla, 143.395 filas».
           */
          id={RESULTS_ANCHOR_ID}
          tabIndex={-1}
          // `grid` is what makes a focusable row legal (and `aria-selected` meaningful); a plain
          // table keeps its default role when the rows are not actionable.
          role={onRowClick ? 'grid' : undefined}
          aria-label={t('cms:table.gridLabel', { name: label })}
          // UX-560: the reader said "row 3 of 50" on a table whose footer said "1–50 de 143.394".
          // The counts are the whole result set, the header row included, and every row carries
          // its absolute index — so paging is audible.
          aria-rowcount={isError || pending ? undefined : total + 1}
          aria-colcount={columns.length}
          aria-busy={busy || undefined}
          className="w-full border-collapse text-left text-[13px] outline-none"
        >
          <thead>
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id} aria-rowindex={1} className="border-b border-line bg-canvas/60">
                {group.headers.map((header) => {
                  const sortable = canSort(header.column.id);
                  const dir = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      aria-sort={
                        sortable
                          ? dir === 'asc'
                            ? 'ascending'
                            : dir === 'desc'
                              ? 'descending'
                              : 'none'
                          : undefined
                      }
                      className="px-4 py-2 text-[12px] font-semibold tracking-wide text-muted uppercase"
                    >
                      {header.isPlaceholder ? null : sortable ? (
                        <button
                          type="button"
                          data-testid={`sort-${header.column.id}`}
                          className="inline-flex items-center gap-1 uppercase hover:text-ink"
                          onClick={() => toggleSort(header.column.id)}
                          title={t('cms:table.sortBy')}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          <span aria-hidden="true">
                            {dir === 'asc' ? '▲' : dir === 'desc' ? '▼' : '↕'}
                          </span>
                          <span className="sr-only">{t('cms:table.sortBy')}</span>
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody ref={body}>
            {isError && (
              <tr>
                <td colSpan={columns.length} className="px-4 py-4">
                  <ListError onRetry={onRetry} />
                </td>
              </tr>
            )}
            {!isError && isLoading && data.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-muted">
                  {t('common:states.loading')}
                </td>
              </tr>
            )}
            {!isError && !isLoading && data.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-muted">
                  {emptyLabel ?? t('cms:table.noRows')}
                </td>
              </tr>
            )}
            {bodyRows.map((row, index) => (
              <tr
                key={row.id}
                data-table-row={index}
                aria-rowindex={(page - 1) * pageSize + index + 2}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                onKeyDown={
                  onRowClick ? (event) => onRowKeyDown(event, row.original, index) : undefined
                }
                onFocus={onRowClick ? () => setActiveRow(index) : undefined}
                tabIndex={onRowClick ? (index === tabStop ? 0 : -1) : undefined}
                aria-label={rowLabel ? rowLabel(row.original) : undefined}
                aria-selected={onRowClick ? selectedRowId === row.id : undefined}
                className={cn(
                  'border-b border-line last:border-0',
                  onRowClick && 'cursor-pointer hover:bg-canvas focus-visible:bg-canvas',
                  selectedRowId === row.id && 'bg-pink/6',
                )}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-4 py-2.5 align-middle">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div
        className="flex flex-wrap items-center justify-between gap-3 text-[12px] text-muted"
        aria-busy={pending || undefined}
      >
        <span data-testid="table-range">
          {isError
            ? '—'
            : pending
              ? t('common:states.loading')
              : t('cms:table.range', {
                  first: formatNumber(first, i18n.language),
                  last: formatNumber(last, i18n.language),
                  total: formatNumber(total, i18n.language),
                })}
        </span>
        <Pager
          page={page}
          pages={pages}
          disabled={pending}
          showJump={!isError && pages > 1}
          onPageChange={onPageChange}
          // UX-570: 99999 and 0 were clamped in silence and the box simply showed a number the
          // operator had not typed.
          onClamp={(wanted) =>
            setStatus(
              t('cms:table.clamped', {
                pages: formatNumber(pages, i18n.language),
                page: formatNumber(wanted, i18n.language),
              }),
            )
          }
        />
      </div>
    </div>
  );
}

/**
 * Prev/next, first/last and a page-number jump.
 *
 * QA 2026-09-15 **UX-535**: the pager was «Anterior / Siguiente» only, so page 2.868 of the
 * 143k-row catalog was 2.867 clicks away and the 343rd page of the review queue the same. The
 * jump is a `number` input submitted with Enter or its own button, clamped to `pages`.
 */
function Pager({
  page,
  pages,
  disabled,
  showJump,
  onPageChange,
  onClamp,
}: {
  page: number;
  pages: number;
  disabled: boolean;
  showJump: boolean;
  onPageChange: (page: number) => void;
  /** The typed page was out of range: called with the page actually opened (UX-570). */
  onClamp?: (page: number) => void;
}) {
  const { t } = useT();
  const [jump, setJump] = useState('');
  const [clamped, setClamped] = useState<number | null>(null);

  const go = () => {
    const wanted = Number.parseInt(jump, 10);
    if (!Number.isFinite(wanted)) return;
    const target = Math.max(1, Math.min(pages, wanted));
    setClamped(target === wanted ? null : target);
    if (target !== wanted) onClamp?.(target);
    onPageChange(target);
    setJump('');
  };

  return (
    <div className="flex flex-wrap items-center gap-1">
      <Button
        variant="secondary"
        size="sm"
        disabled={disabled || page <= 1}
        onClick={() => onPageChange(1)}
        aria-label={t('cms:table.first')}
        data-testid="table-first"
      >
        «
      </Button>
      <Button
        variant="secondary"
        size="sm"
        disabled={disabled || page <= 1}
        onClick={() => onPageChange(page - 1)}
      >
        {t('cms:table.prev')}
      </Button>
      <span className="px-2" data-testid="table-page">
        {disabled ? '…' : t('cms:table.page', { page, pages })}
      </span>
      <Button
        variant="secondary"
        size="sm"
        disabled={disabled || page >= pages}
        onClick={() => onPageChange(page + 1)}
      >
        {t('cms:table.next')}
      </Button>
      <Button
        variant="secondary"
        size="sm"
        disabled={disabled || page >= pages}
        onClick={() => onPageChange(pages)}
        aria-label={t('cms:table.last')}
        data-testid="table-last"
      >
        »
      </Button>
      {showJump && (
        <form
          className="ml-2 flex items-center gap-1"
          // The value is clamped in `go()`; browser constraint validation would silently refuse
          // the submit instead (and jsdom does exactly that).
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            go();
          }}
        >
          <Input
            type="number"
            min={1}
            max={pages}
            inputMode="numeric"
            className="h-8 w-20 text-[12px]"
            aria-label={t('cms:table.goToPage')}
            placeholder={t('cms:table.goToPage')}
            value={jump}
            disabled={disabled}
            onChange={(event) => {
              setClamped(null);
              setJump(event.target.value);
            }}
            data-testid="table-jump"
          />
          <Button variant="secondary" size="sm" type="submit" disabled={disabled || jump === ''}>
            {t('cms:table.go')}
          </Button>
          {clamped !== null && (
            <span className="text-[12px] text-muted" data-testid="table-clamped">
              {t('cms:table.clamped', { pages, page: clamped })}
            </span>
          )}
        </form>
      )}
    </div>
  );
}
